import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ProtectedRoute, PublicRoute, VerifyEmailRoute } from '@/auth/ProtectedRoute';
import { HiddenSessionsRoute } from '@/auth/HiddenSessionsRoute';
import { SuperAdminRoute } from '@/auth/SuperAdminRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { WelcomeShell } from '@/components/layout/WelcomeShell';
import { WelcomeGate } from '@/auth/WelcomeGate';
import { useExtensionMessageListener, hasExtension, setExtensionInstalled } from '@/hooks/useExtensionUtil';
import { isExtensionApp } from '@/utils/platform';
import { DesktopShellExtras } from '@/desktop/DesktopShellExtras';
import { useSessionsStore } from '@/stores/sessions-store';

// Lazy load routes for code splitting
const DashboardPage = lazy(() => import('@/pages/home/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const TabsIndexPage = lazy(() => import('@/pages/home/TabsIndexPage').then((m) => ({ default: m.TabsIndexPage })));
const SessionLayout = lazy(() => import('@/pages/home/SessionLayout').then((m) => ({ default: m.SessionLayout })));
const TabsListContent = lazy(() => import('@/pages/home/TabsListContent').then((m) => ({ default: m.TabsListContent })));
const BookmarksContent = lazy(() => import('@/pages/home/BookmarksContent').then((m) => ({ default: m.BookmarksContent })));
const NotesContent = lazy(() => import('@/pages/home/NotesContent').then((m) => ({ default: m.NotesContent })));
const HighlighterContent = lazy(() => import('@/pages/home/HighlighterContent').then((m) => ({ default: m.HighlighterContent })));
const TasksContent = lazy(() => import('@/pages/home/TasksContent').then((m) => ({ default: m.TasksContent })));
const ResearchWorkspaceShell = lazy(() =>
  import('@/components/research/ResearchWorkspaceShell').then((m) => ({ default: m.ResearchWorkspaceShell }))
);
const ResearchOverviewPage = lazy(() => import('@/pages/research/ResearchOverviewPage'));
const AddLibraryWizardPage = lazy(() => import('@/pages/research/AddLibraryWizardPage'));
const AddKnowledgeWizardPage = lazy(() => import('@/pages/research/AddKnowledgeWizardPage'));
const AddIdeaWizardPage = lazy(() => import('@/pages/research/AddIdeaWizardPage'));
const ResearchKnowledgePage = lazy(() => import('@/pages/research/ResearchKnowledgePage'));
const ResearchUnderstandingPage = lazy(() => import('@/pages/research/ResearchUnderstandingPage'));
const ResearchTopicsPage = lazy(() => import('@/pages/research/ResearchTopicsPage'));
const ResearchTopicDetailPage = lazy(() => import('@/pages/research/ResearchTopicDetailPage'));
const ResearchDecisionsPage = lazy(() => import('@/pages/research/ResearchDecisionsPage'));
const ResearchWorkflowsPage = lazy(() => import('@/pages/research/ResearchWorkflowsPage'));
const ResearchCompetitorsPage = lazy(() => import('@/pages/research/ResearchCompetitorsPage'));
const CompetitorAnalysisPage = lazy(() => import('@/pages/research/CompetitorAnalysisPage'));
const ResearchLibraryPage = lazy(() => import('@/pages/research/ResearchLibraryPage'));
const ResearchIdeasPage = lazy(() => import('@/pages/research/ResearchIdeasPage'));
const ResearchOutputsPage = lazy(() => import('@/pages/research/ResearchOutputsPage'));
const ResearchOutputDetailPage = lazy(() => import('@/pages/research/ResearchOutputDetailPage'));
const InitialResearchWizardPage = lazy(() => import('@/pages/research/InitialResearchWizardPage'));
const ResearchMissionPage = lazy(() => import('@/pages/research/ResearchMissionPage'));
const ResearchAskPage = lazy(() => import('@/pages/research/ResearchAskPage'));
const BookmarksPage = lazy(() => import('@/pages/home/BookmarksPage').then((m) => ({ default: m.BookmarksPage })));
const NotesPage = lazy(() => import('@/pages/home/NotesPage').then((m) => ({ default: m.NotesPage })));
const HighlighterPage = lazy(() => import('@/pages/highlighter/HighlighterPage').then((m) => ({ default: m.HighlighterPage })));
const TasksPage = lazy(() => import('@/pages/tasks/TasksPage').then((m) => ({ default: m.TasksPage })));
const ChatPage = lazy(() => import('@/pages/chat/ChatPage').then((m) => ({ default: m.ChatPage })));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const AssistantPage = lazy(() => import('@/pages/assistant/AssistantPage').then((m) => ({ default: m.AssistantPage })));
const NewNotePage = lazy(() => import('@/pages/home/NewNotePage').then((m) => ({ default: m.NewNotePage })));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage').then((m) => ({ default: m.SignupPage })));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const JoinInvitePage = lazy(() => import('@/pages/auth/JoinInvitePage').then((m) => ({ default: m.JoinInvitePage })));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SharedWithMePage = lazy(() => import('@/pages/shared/SharedWithMePage').then((m) => ({ default: m.SharedWithMePage })));
const PublicSharePage = lazy(() => import('@/pages/shared/PublicSharePage').then((m) => ({ default: m.PublicSharePage })));
const SharedItemPage = lazy(() => import('@/pages/shared/SharedItemPage').then((m) => ({ default: m.SharedItemPage })));
const ExtensionIframeAddTrovePage = lazy(() =>
  import('@/pages/extension/ExtensionIframeAddTrovePage').then((m) => ({ default: m.ExtensionIframeAddTrovePage }))
);
const ExtensionIframeNewNotePage = lazy(() =>
  import('@/pages/extension/ExtensionIframeNewNotePage').then((m) => ({ default: m.ExtensionIframeNewNotePage }))
);
const HiddenSessionsShellPage = lazy(() =>
  import('@/pages/extension/HiddenSessionsShellPage').then((m) => ({ default: m.HiddenSessionsShellPage }))
);
const ExtensionPopupBookmarksPage = lazy(() =>
  import('@/pages/extension/ExtensionPopupBookmarksPage').then((m) => ({
    default: m.ExtensionPopupBookmarksPage,
  }))
);
const ExtensionPopupNotesPage = lazy(() =>
  import('@/pages/extension/ExtensionPopupNotesPage').then((m) => ({
    default: m.ExtensionPopupNotesPage,
  }))
);
const ExtensionPopupNewNotePage = lazy(() =>
  import('@/pages/extension/ExtensionPopupNewNotePage').then((m) => ({
    default: m.ExtensionPopupNewNotePage,
  }))
);
const ExtensionPopupAddTrovePage = lazy(() =>
  import('@/pages/extension/ExtensionPopupAddTrovePage').then((m) => ({
    default: m.ExtensionPopupAddTrovePage,
  }))
);
const WelcomePage = lazy(() =>
  import('@/pages/welcome/WelcomePage').then((m) => ({ default: m.WelcomePage }))
);
const ThemeSwatchPage = lazy(() =>
  import('@/pages/dev/ThemeSwatchPage').then((m) => ({ default: m.ThemeSwatchPage }))
);
const HelpLayout = lazy(() =>
  import('@/pages/help/HelpLayout').then((m) => ({ default: m.HelpLayout }))
);
const HelpCenterPage = lazy(() =>
  import('@/pages/help/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage }))
);
const HelpArticlePage = lazy(() =>
  import('@/pages/help/HelpArticlePage').then((m) => ({ default: m.HelpArticlePage }))
);

// Admin console: separate code-split chunk via the inline /* webpackChunkName: "admin" */
// magic comment on each dynamic import below. Vite/Rollup inlines all imports tagged with
// the same chunk name into one chunk, so a non-super-admin's tab never downloads the
// admin surface. Access is URL-only — no link from the main nav.
const AdminLayout = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/components/admin/AdminLayout').then((m) => ({
    default: m.AdminLayout,
  }))
);
const AdminDashboardPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  }))
);
const AdminTeamPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/team/AdminTeamPage').then((m) => ({
    default: m.AdminTeamPage,
  }))
);
const AdminPendingActionsPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/pending/AdminPendingActionsPage').then((m) => ({
    default: m.AdminPendingActionsPage,
  }))
);
const AdminAuditPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/audit/AdminAuditPage').then((m) => ({
    default: m.AdminAuditPage,
  }))
);
const AdminAccountsSearchPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountsSearchPage').then((m) => ({
    default: m.AdminAccountsSearchPage,
  }))
);
const AdminAccountDetailPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountDetailPage').then((m) => ({
    default: m.AdminAccountDetailPage,
  }))
);
const AdminAccountBillingPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountBillingPage').then((m) => ({
    default: m.AdminAccountBillingPage,
  }))
);
const AdminAccountAiPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountAiPage').then((m) => ({
    default: m.AdminAccountAiPage,
  }))
);
const AdminAccountApiPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountApiPage').then((m) => ({
    default: m.AdminAccountApiPage,
  }))
);
const AdminAccountContentPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/accounts/AdminAccountContentPage').then((m) => ({
    default: m.AdminAccountContentPage,
  }))
);
const AdminSchedulersPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/jobs/AdminSchedulersPage').then((m) => ({
    default: m.AdminSchedulersPage,
  }))
);
const AdminAbusePage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/abuse/AdminAbusePage').then((m) => ({
    default: m.AdminAbusePage,
  }))
);
const AdminKillSwitchesPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/abuse/AdminKillSwitchesPage').then((m) => ({
    default: m.AdminKillSwitchesPage,
  }))
);
const AdminBetaPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/beta/AdminBetaPage').then((m) => ({
    default: m.AdminBetaPage,
  }))
);
const AdminHealthPage = lazy(() =>
  import(/* webpackChunkName: "admin" */ '@/pages/admin/health/AdminHealthPage').then((m) => ({
    default: m.AdminHealthPage,
  }))
);

/** Preserve query string when redirecting legacy `/index/*` invite paths. */
function LegacyInviteRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Persistent Layout Wrapper
function AppLayout() {
  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
}

// Subsection components for Settings (keeping them as direct imports for now since they are in components/)
import { AccountSettings } from '@/components/settings/AccountSettings';
import { AdminSettingsGuard } from '@/components/settings/AdminSettingsGuard';
import { UsersSettings } from '@/components/settings/UsersSettings';
import { CompanyAccountSettings } from '@/components/settings/CompanyAccountSettings';
import { DownloadsSettings } from '@/components/settings/DownloadsSettings';
import { ImportBookmarksSettings } from '@/components/settings/ImportBookmarksSettings';
import { SubscriptionSettings } from '@/components/settings/SubscriptionSettings';
import { AiUsageSettings } from '@/components/settings/AiUsageSettings';
import { VaultSettings } from '@/components/settings/VaultSettings';
import { StorageSettings } from '@/components/settings/StorageSettings';
import { NotesLockSettings } from '@/components/settings/NotesLockSettings';
import { ImportExportSettings } from '@/components/settings/ImportExportSettings';

const WorkInProgress = () => {
  const { t } = useTranslation('settings');
  return (
    <div className="flex flex-col items-center justify-center space-y-4 py-20 text-center animate-in fade-in duration-500">
      <div className="rounded-3xl bg-slate-800/40 p-4 text-slate-500">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-slate-600" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-slate-200">{t('wip.title')}</h3>
        <p className="mx-auto mt-1 max-w-xs text-slate-500">{t('wip.body')}</p>
      </div>
    </div>
  );
};

/** Sets up window message listener for troves-content (e.g. extension callbacks). */
function ExtensionMessageListener() {
  const { setSessionUpdate } = useSessionsStore();

  useEffect(() => {
    if (isExtensionApp()) {
      // Extension UI pages are the extension — no content-script tabregistered handshake.
      setExtensionInstalled(true);
      return;
    }
    // Register this tab with the content script
    window.postMessage({ from: 'troves', action: 'registerTab' }, '*');
    // Kick off the singleton timer so detection resolves even if
    // no component has awaited hasExtension() yet.
    hasExtension();
  }, []);

  useExtensionMessageListener({
    onSessions: (data) => { setSessionUpdate(data as Record<string, unknown>); },
    // tabregistered arrived → immediately resolve the singleton promise
    onExtensionDetected: () => { setExtensionInstalled(true); },
  });
  return null;
}

/**
 * Application router with lazy-loaded routes
 * Each route is code-split for optimal bundle size
 */
export function Router() {
  return (
    <>
      <ExtensionMessageListener />
      <DesktopShellExtras />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>

          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailRoute><VerifyEmailPage /></VerifyEmailRoute>} />
          <Route path="/join-invite" element={<ProtectedRoute><JoinInvitePage /></ProtectedRoute>} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/t/:publicLinkId" element={<PublicSharePage />} />

          {import.meta.env.DEV ? (
            <Route path="/dev/theme" element={<ThemeSwatchPage />} />
          ) : null}

          {/* Legacy extension / email hash paths (`#/index/joininvite`, etc.) */}
          <Route path="/index/extensionlogin" element={<Navigate to="/login" replace />} />
          <Route path="/index/joininvite" element={<LegacyInviteRedirect to="/join-invite" />} />
          <Route path="/index/acceptinvite" element={<LegacyInviteRedirect to="/accept-invite" />} />
          <Route path="/index/tabs/*" element={<Navigate to="/tabs" replace />} />
          <Route path="/index/sessions" element={<Navigate to="/sessions" replace />} />
          <Route
            path="/sessions"
            element={
              <HiddenSessionsRoute>
                <HiddenSessionsShellPage />
              </HiddenSessionsRoute>
            }
          />
          <Route
            path="/index/addtrove"
            element={
              <ProtectedRoute>
                <ExtensionIframeAddTrovePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/index/newnote"
            element={
              <ProtectedRoute>
                <ExtensionIframeNewNotePage />
              </ProtectedRoute>
            }
          />

          {/* Extension popup iframe — full library UI without MainLayout */}
          <Route
            path="/popup/troves"
            element={
              <ProtectedRoute>
                <ExtensionPopupBookmarksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/addtrove"
            element={
              <ProtectedRoute>
                <ExtensionPopupAddTrovePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/notes"
            element={
              <ProtectedRoute>
                <ExtensionPopupNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/notes/folder/:folderId"
            element={
              <ProtectedRoute>
                <ExtensionPopupNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/notes/tags/:tagId"
            element={
              <ProtectedRoute>
                <ExtensionPopupNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/notes/new"
            element={
              <ProtectedRoute>
                <ExtensionPopupNewNotePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/popup/notes/edit/:noteId"
            element={
              <ProtectedRoute>
                <ExtensionPopupNewNotePage />
              </ProtectedRoute>
            }
          />

          {/* First-run welcome (no sidebar) */}
          <Route element={<ProtectedRoute><WelcomeShell /></ProtectedRoute>}>
            <Route path="/welcome" element={<WelcomePage />} />
          </Route>

          {/* Protected Internal Routes with persistent Sidebar */}
          <Route element={<ProtectedRoute><WelcomeGate /></ProtectedRoute>}>
            <Route element={<AppLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/tabs" element={<TabsIndexPage />} />
            {/* Session-specific routes with static sidebar/topbar */}
            <Route path="/tabs/:sessionId" element={<SessionLayout />}>
              <Route path="list" element={<TabsListContent />} />
              <Route path="research" element={<ResearchWorkspaceShell />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<ResearchOverviewPage />} />
                <Route path="competitors" element={<ResearchCompetitorsPage />} />
                <Route path="competitors/:competitorId" element={<CompetitorAnalysisPage />} />
                <Route path="library" element={<ResearchLibraryPage />} />
                <Route path="library/add" element={<AddLibraryWizardPage />} />
                <Route path="knowledge" element={<ResearchKnowledgePage />} />
                <Route path="knowledge/add" element={<AddKnowledgeWizardPage />} />
                <Route path="topics" element={<ResearchTopicsPage />} />
                <Route path="topics/:topicId" element={<ResearchTopicDetailPage />} />
                <Route path="decisions" element={<ResearchDecisionsPage />} />
                <Route path="understanding" element={<ResearchUnderstandingPage />} />
                <Route path="workflows" element={<ResearchWorkflowsPage />} />
                <Route path="ideas" element={<ResearchIdeasPage />} />
                <Route path="ideas/add" element={<AddIdeaWizardPage />} />
                <Route path="briefs" element={<Navigate to="outputs" replace />} />
                <Route path="outputs" element={<ResearchOutputsPage />} />
                <Route path="outputs/:outputId" element={<ResearchOutputDetailPage />} />
                <Route path="initial" element={<InitialResearchWizardPage />} />
                <Route path="missions/new" element={<ResearchMissionPage />} />
                <Route path="ai" element={<ResearchAskPage />} />
              </Route>
              <Route path="bookmark" element={<BookmarksContent />} />
              <Route path="notes" element={<NotesContent />} />
              <Route path="notes/new" element={<NewNotePage />} />
              <Route path="notes/edit/:noteId" element={<NewNotePage />} />
              <Route path="highlighter" element={<HighlighterContent />} />
              <Route path="tasks" element={<TasksContent />} />
              <Route index element={<Navigate to="list" replace />} />
            </Route>
            {/* Dynamic Trove Routes - pointing to same component but distinct paths to avoid double-stacking */}
            <Route path="/shared-with-me" element={<SharedWithMePage />} />
            <Route path="/shared/:shareId" element={<SharedItemPage />} />
            <Route path="/troves" element={<BookmarksPage />} />
            <Route path="/troves/:view" element={<BookmarksPage />} />
            <Route path="/troves/folder/:folderId" element={<BookmarksPage />} />
            <Route path="/troves/tags/:tagId" element={<BookmarksPage />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route path="/notes/folder/:folderId" element={<NotesPage />} />
            <Route path="/notes/tags/:tagId" element={<NotesPage />} />
            <Route path="/notes/new" element={<NewNotePage />} />
            <Route path="/notes/edit/:noteId" element={<NewNotePage />} />
            <Route path="/highlighter" element={<HighlighterPage />} />
            <Route path="/highlighter/:sourceId" element={<HighlighterPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:projectId" element={<TasksPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/help" element={<HelpLayout />}>
              <Route index element={<HelpCenterPage />} />
              <Route path=":category/:slug" element={<HelpArticlePage />} />
            </Route>

            {/* Nested Settings Routes */}
            <Route path="/settings" element={<SettingsPage />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<AccountSettings />} />
              <Route path="accounts" element={<CompanyAccountSettings />} />
              <Route path="team" element={<UsersSettings />} />
              <Route path="import" element={<AdminSettingsGuard><ImportBookmarksSettings /></AdminSettingsGuard>} />
              <Route path="data-export" element={<AdminSettingsGuard><ImportExportSettings /></AdminSettingsGuard>} />
              <Route path="subscription" element={<SubscriptionSettings />} />
              <Route path="ai-usage" element={<AiUsageSettings />} />
              <Route
                path="ai-suggestions"
                element={<Navigate to="/settings/profile?tab=ai#ai-privacy" replace />}
              />
              <Route path="ai-byok" element={<Navigate to="/settings/profile?tab=integrations#byok" replace />} />
              <Route
                path="ai-preferences"
                element={<Navigate to="/settings/profile?tab=ai#ai-privacy" replace />}
              />
              <Route path="vault" element={<VaultSettings />} />
              <Route path="storage" element={<StorageSettings />} />
              <Route path="notes-lock" element={<NotesLockSettings />} />
              <Route path="downloads" element={<DownloadsSettings />} />
              <Route path="help" element={<Navigate to="/help" replace />} />

              <Route path="general" element={<WorkInProgress />} />
              <Route path="integrations" element={<Navigate to="/settings/profile?tab=mcp" replace />} />
              <Route path="notifications" element={<WorkInProgress />} />
              <Route path="security" element={<WorkInProgress />} />
            </Route>
            </Route>
          </Route>

          {/*
            Super-admin console (Slice 0+).
            URL-only entry; no link in MainLayout. Lives outside ProtectedRoute
            so AdminLayout owns its own chrome (no main app sidebar/topbar).
          */}
          <Route
            path="/admin"
            element={
              <SuperAdminRoute>
                <AdminLayout />
              </SuperAdminRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="accounts" element={<AdminAccountsSearchPage />} />
            <Route path="accounts/:aid" element={<AdminAccountDetailPage />} />
            <Route path="accounts/:aid/billing" element={<AdminAccountBillingPage />} />
            <Route path="accounts/:aid/ai" element={<AdminAccountAiPage />} />
            <Route path="accounts/:aid/api" element={<AdminAccountApiPage />} />
            <Route path="accounts/:aid/content" element={<AdminAccountContentPage />} />
            <Route path="jobs" element={<AdminSchedulersPage />} />
            <Route path="abuse" element={<AdminAbusePage />} />
            <Route path="kill-switches" element={<AdminKillSwitchesPage />} />
            <Route path="beta" element={<AdminBetaPage />} />
            <Route path="health" element={<AdminHealthPage />} />
            <Route path="team" element={<AdminTeamPage />} />
            <Route path="pending" element={<AdminPendingActionsPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
